import {
  AppBar,
  Button,
  createStyles,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core"
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js"
import clsx from "clsx"
import { ChangeEvent, useState } from "react"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { BounceLoader } from "react-spinners"
import { useGetClientDataQuery } from "../../generated/graphql"
import { useAIServices } from "../../hooks/useAIServices"
import { useClientConnectionStatus } from "../../hooks/useClientConnectionStatus"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useNavigation } from "../../hooks/useNavigation"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import { useSession } from "../../hooks/useSession"
import { useUser } from "../../hooks/useUser"
import { Role } from "../../utils/constants"
import { generatePdf } from "../../utils/generatePdf"
import { colors } from "../../utils/theme"
import ChevronLeftIcon from "../atoms/ChevronLeftIcon"
import { HeaderHeight } from "../molecules/Header"
import MoodHelpIcon from "../molecules/MoodHelpIcon"
import { a11yPropsForTabs, TabPanel } from "../molecules/Tabs"
import AI from "../organisms/AI"
import AssessmentOverview from "../organisms/assessmentOverview"
import ClientMobileSettings from "../organisms/ClientMobileSettings"
import EmotionalCooldownRatings from "../organisms/EmotionalCooldownRatings"
import Insights from "../organisms/Insights/Insights"
import MoodBarChart from "../organisms/moodBarChart/MoodBarChart"
import QuestionnairesOverview from "../organisms/QuestionnairesOverview"
import PlanOverview from "../organisms/safetyPlan/PlanOverview"
import Schedule from "../organisms/Schedule"
import ScheduleOverview from "../organisms/ScheduleOverview"
import Trends from "../organisms/Trends"
import TrendsOverview from "../organisms/TrendsOverview"
import { BRITE_SAFETY_PLAN } from "../../utils/constants"

interface Params {
  clientId: string
}

const MyPractitionerClientDetail = () => {
  const [tab, setTab] = useState<number>(0)
  const params: Params = useParams()
  const classes = useStyles()
  const graphqlClient = useGraphQLClient()
  const clientData = useGetClientDataQuery(graphqlClient, {
    clientId: params.clientId,
  })
  const history = useHistory()
  const { toDashboard } = useNavigation()
  const { session } = useSession()
  const { user } = useUser()
  let userOrgId = "none"
  if (user?.organizations) {
    userOrgId = `${user?.organizations[0].id}-${user?.organizations[0].name}`
  }

  const location = useLocation<{ practitionerId: string }>()
  const practitionerId = location.state?.practitionerId ?? "" // supervisor viewing a practitioner's client
  const supervisorId =
    session?.role === Role.WEB_ROLE ? session.userId ?? "" : ""

  const { isAIServicesMember } = useAIServices()
  const [cache, setCache] = useState<string>("")
  const updateCache = (val: any) => setCache(val)

  const { canSchedule } = useClientConnectionStatus(
    params.clientId,
    practitionerId
  )

  // Refer to BRD, AI Services Part 1 (Demonstration)
  const preSessionPrompts: any[] = [
    {
      id: 1,
      heading: "Brief Case Overview",
      prompt:
        "Please give me a summary of the client’s presenting goals, history of the symptoms, behavioral data and the treatment plan so far in approximately 300 words.",
    },
    {
      id: 2,
      heading: "Homework Tasks and Adherence Summary",
      prompt:
        "Remind me of the nudge schedule that has been in place since our last session and summarize the client’s rates of completion of the delivered interventions.",
    },
  ]
  const postSessionPrompts: any[] = [
    {
      id: 3,
      heading: "Draft Session Progress Notes",
      prompt:
        "Please draft a session note with the client specific details between now and last session. If the date of last session is not defined, please assume it was two weeks ago.",
    },
    {
      id: 4,
      heading:
        "Second Opinion: \n" +
        "Ask the AI Clinical Advisor for Strategies to Consider with this Client",
      prompt:
        "Please review the current treatment plan as reflected in the nudge schedule, and suggest other evidence-based strategies that could be considered for this case.",
    },
    {
      id: 5,
      heading: "Hard Session? Suggest a Brief Self-Care Activity",
      prompt:
        "Please select at random one of the self-care activities from the resource library. Display the activity.",
    },
  ]

  const { showBriteFeatures } = useOrganizationFeatureFlags()

  const appInsights = useAppInsightsContext()
  // Empty strings are schema placeholders for useTrackEvent; real values are
  // passed at click time when trackClientTabs({ tab, org, practitionerId }) runs.
  const trackClientTabs = useTrackEvent(
    appInsights,
    "Client Detail Tab Click",
    {
      tab: "",
      org: "",
      practitionerId: "",
      supervisorId: "",
    }
  )

  enum TIME_PERIOD {
    WEEK = "week",
    MONTH = "month",
  }

  const [timePeriod, setTimePeriod] = useState<TIME_PERIOD>(TIME_PERIOD.WEEK)

  const client = clientData.data?.users_by_pk

  const [loading, setLoading] = useState(false)

  const exportToPdf = async () => {
    setLoading(true)
    await generatePdf(client?.last_name || "", "Overview")
    setLoading(false)
  }

  if (!client) {
    return (
      <div className={clsx(classes.clientDetail, classes.loading)}>
        Loading...
      </div>
    )
  }

  const preSessionTxt = `Your Vira AI Assistant will help you prepare for your appointment with ${client?.first_name}.`
  const postSessionTxt = "How can I help you complete your post-session tasks?"
  const checkingTotal = client.mood_measurements.length
  const totalCheckins = checkingTotal !== undefined ? checkingTotal : 0

  const goBack = () => {
    if (session?.role === Role.WEB_ROLE) {
      history.goBack()
    } else {
      toDashboard()
    }
  }
  return (
    <div className={classes.clientDetail}>
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 5000,
        }}
        id="spinner"
      >
        <BounceLoader
          color={"rgb(117, 145, 228)"}
          size={75}
          loading={loading}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <div style={{ display: loading ? "block" : "none" }}>
          Generating PDF
        </div>
      </div>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <button className={classes.navigation} onClick={goBack}>
            <ChevronLeftIcon className={classes.chevron} />
            <Typography variant="h2" className={classes.title}>
              {client.first_name} {client.last_name}
            </Typography>
          </button>
        </div>
        <AppBar position="static" className={classes.tabs}>
          {isAIServicesMember && showBriteFeatures && (
            <Tabs
              value={tab}
              onChange={(event: ChangeEvent<{}>, newValue: number) =>
                setTab(newValue)
              }
              variant="scrollable"
              scrollButtons="auto"
              aria-label="tabs"
            >
              <Tab
                label="SESSION PREP"
                {...a11yPropsForTabs(0)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SESSION PREP",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="OVERVIEW"
                {...a11yPropsForTabs(1)}
                onClick={() =>
                  trackClientTabs({
                    tab: "OVERVIEW",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SAFETY PLAN"
                {...a11yPropsForTabs(2)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SAFETY PLAN",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="TRENDS"
                {...a11yPropsForTabs(3)}
                onClick={() => {
                  trackClientTabs({
                    tab: "TRENDS",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }}
              />
              <Tab
                label="ASSESSMENT"
                {...a11yPropsForTabs(4)}
                onClick={() =>
                  trackClientTabs({
                    tab: "ASSESSMENT",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SCHEDULE"
                {...a11yPropsForTabs(5)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SCHEDULE",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="POST SESSION"
                {...a11yPropsForTabs(6)}
                onClick={() =>
                  trackClientTabs({
                    tab: "POST SESSION",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="APP INFO"
                {...a11yPropsForTabs(7)}
                onClick={() =>
                  trackClientTabs({
                    tab: "APP INFO",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
            </Tabs>
          )}
          {isAIServicesMember && !showBriteFeatures && (
            <Tabs
              value={tab}
              onChange={(event: ChangeEvent<{}>, newValue: number) =>
                setTab(newValue)
              }
              variant="scrollable"
              scrollButtons="auto"
              aria-label="tabs"
            >
              <Tab
                label="SESSION PREP"
                {...a11yPropsForTabs(0)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SESSION PREP",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="OVERVIEW"
                {...a11yPropsForTabs(1)}
                onClick={() =>
                  trackClientTabs({
                    tab: "OVERVIEW",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="TRENDS"
                {...a11yPropsForTabs(2)}
                onClick={() => {
                  trackClientTabs({
                    tab: "TRENDS",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }}
              />
              <Tab
                label="ASSESSMENT"
                {...a11yPropsForTabs(3)}
                onClick={() =>
                  trackClientTabs({
                    tab: "ASSESSMENT",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SCHEDULE"
                {...a11yPropsForTabs(4)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SCHEDULE",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="POST SESSION"
                {...a11yPropsForTabs(5)}
                onClick={() =>
                  trackClientTabs({
                    tab: "POST SESSION",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="APP INFO"
                {...a11yPropsForTabs(6)}
                onClick={() =>
                  trackClientTabs({
                    tab: "APP INFO",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
            </Tabs>
          )}
          {!isAIServicesMember && showBriteFeatures && (
            <Tabs
              value={tab}
              onChange={(event: ChangeEvent<{}>, newValue: number) =>
                setTab(newValue)
              }
              variant="scrollable"
              scrollButtons="auto"
              aria-label="tabs"
            >
              <Tab
                label="OVERVIEW"
                {...a11yPropsForTabs(0)}
                onClick={() =>
                  trackClientTabs({
                    tab: "OVERVIEW",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SAFETY PLAN"
                {...a11yPropsForTabs(1)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SAFETY PLAN",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="TRENDS"
                {...a11yPropsForTabs(2)}
                onClick={() =>
                  trackClientTabs({
                    tab: "TRENDS",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="ASSESSMENT"
                {...a11yPropsForTabs(3)}
                onClick={() =>
                  trackClientTabs({
                    tab: "ASSESSMENT",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SCHEDULE"
                {...a11yPropsForTabs(4)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SCHEDULE",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="APP INFO"
                {...a11yPropsForTabs(5)}
                onClick={() =>
                  trackClientTabs({
                    tab: "APP INFO",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
            </Tabs>
          )}
          {!isAIServicesMember && !showBriteFeatures && (
            <Tabs
              value={tab}
              onChange={(event: ChangeEvent<{}>, newValue: number) =>
                setTab(newValue)
              }
              variant="scrollable"
              scrollButtons="auto"
              aria-label="tabs"
            >
              <Tab
                label="OVERVIEW"
                {...a11yPropsForTabs(0)}
                onClick={() =>
                  trackClientTabs({
                    tab: "OVERVIEW",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="TRENDS"
                {...a11yPropsForTabs(1)}
                onClick={() =>
                  trackClientTabs({
                    tab: "TRENDS",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="ASSESSMENT"
                {...a11yPropsForTabs(2)}
                onClick={() =>
                  trackClientTabs({
                    tab: "ASSESSMENT",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="SCHEDULE"
                {...a11yPropsForTabs(3)}
                onClick={() =>
                  trackClientTabs({
                    tab: "SCHEDULE",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
              <Tab
                label="APP INFO"
                {...a11yPropsForTabs(4)}
                onClick={() =>
                  trackClientTabs({
                    tab: "APP INFO",
                    org: userOrgId,
                    practitionerId: practitionerId,
                    supervisorId: supervisorId,
                  })
                }
              />
            </Tabs>
          )}
        </AppBar>
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          {isAIServicesMember && showBriteFeatures && (
            <>
              <TabPanel value={tab === 0} index={0}>
                <AI
                  client={client}
                  userId={practitionerId}
                  prompts={preSessionPrompts}
                  text={preSessionTxt}
                  tab="pre-session"
                  updateCache={updateCache}
                  cache={cache}
                />
              </TabPanel>
              <TabPanel value={tab === 1} index={1}>
                <TrendsOverview client={client} openTrends={() => setTab(3)} />
                <div className={classes.chartsAndSchedule}>
                  <div>
                    <div
                      className={clsx(
                        classes.titleIcon,
                        classes.subtitleMargin
                      )}
                    >
                      <Typography variant="h3" className={classes.subtitle}>
                        Mood
                      </Typography>
                      <MoodHelpIcon
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                      />
                    </div>
                    <div className={classes.chart}>
                      <MoodBarChart
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                        setTimePeriod={setTimePeriod}
                      />
                    </div>
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Distress Ratings
                    </Typography>
                    <Typography
                      variant="body2"
                      className={clsx(
                        classes.subtitle,
                        classes.subtitleMargin,
                        classes.distressRatings
                      )}
                    >
                      Users rate their distress before and after completing an
                      activity (emotional cooldown). 1 = Least distressed, 5 =
                      most distressed
                    </Typography>
                    <EmotionalCooldownRatings client={client} />
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Insights
                    </Typography>
                    <div className={classes.chart}>
                      <Insights checkIns={totalCheckins} client={client} />
                    </div>
                    <QuestionnairesOverview
                      client={client}
                      canSchedule={canSchedule}
                    />
                  </div>
                  <ScheduleOverview client={client} canSchedule={canSchedule} />
                </div>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{ paddingTop: "10px" }}
                >
                  <Button
                    variant="outlined"
                    onClick={exportToPdf}
                    style={{ textTransform: "none" }}
                    disabled={loading}
                  >
                    Export
                  </Button>
                </Grid>
              </TabPanel>

              <TabPanel value={tab === 2} index={2}>
                <PlanOverview client={client} planName={BRITE_SAFETY_PLAN} />
              </TabPanel>
              <TabPanel value={tab === 3} index={3}>
                <Trends client={client} />
              </TabPanel>
              <TabPanel value={tab === 4} index={4}>
                <AssessmentOverview client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 5} index={5}>
                <Schedule client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 6} index={6}>
                <AI
                  client={client}
                  userId={practitionerId}
                  prompts={postSessionPrompts}
                  text={postSessionTxt}
                  tab="post-session"
                  updateCache={updateCache}
                  cache={cache}
                />
              </TabPanel>
              <TabPanel value={tab === 7} index={7}>
                <ClientMobileSettings client={client} />
              </TabPanel>
            </>
          )}
          {isAIServicesMember && !showBriteFeatures && (
            <>
              <TabPanel value={tab === 0} index={0}>
                <AI
                  client={client}
                  userId={practitionerId}
                  prompts={preSessionPrompts}
                  text={preSessionTxt}
                  tab="pre-session"
                  updateCache={updateCache}
                  cache={cache}
                />
              </TabPanel>
              <TabPanel value={tab === 1} index={1}>
                <TrendsOverview client={client} openTrends={() => setTab(2)} />
                <div className={classes.chartsAndSchedule}>
                  <div>
                    <div
                      className={clsx(
                        classes.titleIcon,
                        classes.subtitleMargin
                      )}
                    >
                      <Typography variant="h3" className={classes.subtitle}>
                        Mood
                      </Typography>
                      <MoodHelpIcon
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                      />
                    </div>
                    <div className={classes.chart}>
                      <MoodBarChart
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                        setTimePeriod={setTimePeriod}
                      />
                    </div>
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Insights
                    </Typography>
                    <div className={classes.chart}>
                      <Insights checkIns={totalCheckins} client={client} />
                    </div>
                    <QuestionnairesOverview
                      client={client}
                      canSchedule={canSchedule}
                    />
                  </div>
                  <ScheduleOverview client={client} canSchedule={canSchedule} />
                </div>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{ paddingTop: "10px" }}
                >
                  <Button
                    variant="outlined"
                    onClick={exportToPdf}
                    style={{ textTransform: "none" }}
                    disabled={loading}
                  >
                    Export
                  </Button>
                </Grid>
              </TabPanel>
              <TabPanel value={tab === 2} index={2}>
                <Trends client={client} />
              </TabPanel>
              <TabPanel value={tab === 3} index={3}>
                <AssessmentOverview client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 4} index={4}>
                <Schedule client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 5} index={5}>
                <AI
                  client={client}
                  userId={practitionerId}
                  prompts={postSessionPrompts}
                  text={postSessionTxt}
                  tab="post-session"
                  updateCache={updateCache}
                  cache={cache}
                />
              </TabPanel>
              <TabPanel value={tab === 6} index={6}>
                <ClientMobileSettings client={client} />
              </TabPanel>
            </>
          )}
          {!isAIServicesMember && showBriteFeatures && (
            <>
              <TabPanel value={tab === 0} index={0}>
                <TrendsOverview client={client} openTrends={() => setTab(2)} />
                <div className={classes.chartsAndSchedule}>
                  <div>
                    <div
                      className={clsx(
                        classes.titleIcon,
                        classes.subtitleMargin
                      )}
                    >
                      <Typography variant="h3" className={classes.subtitle}>
                        Mood
                      </Typography>
                      <MoodHelpIcon
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                      />
                    </div>
                    <div className={classes.chart}>
                      <MoodBarChart
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                        setTimePeriod={setTimePeriod}
                      />
                    </div>
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Distress Ratings
                    </Typography>
                    <Typography
                      variant="body2"
                      className={clsx(
                        classes.subtitle,
                        classes.subtitleMargin,
                        classes.distressRatings
                      )}
                    >
                      Users rate their distress before and after completing an
                      activity (emotional cooldown). 1 = Least distressed, 5 =
                      most distressed
                    </Typography>
                    <EmotionalCooldownRatings client={client} />
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Insights
                    </Typography>
                    <div className={classes.chart}>
                      <Insights checkIns={totalCheckins} client={client} />
                    </div>
                    <QuestionnairesOverview
                      client={client}
                      canSchedule={canSchedule}
                    />
                  </div>
                  <ScheduleOverview client={client} canSchedule={canSchedule} />
                </div>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{ paddingTop: "10px" }}
                >
                  <Button
                    variant="outlined"
                    onClick={exportToPdf}
                    style={{ textTransform: "none" }}
                    disabled={loading}
                  >
                    Export
                  </Button>
                </Grid>
              </TabPanel>

              <TabPanel value={tab === 1} index={1}>
                <PlanOverview client={client} planName={BRITE_SAFETY_PLAN} />
              </TabPanel>
              <TabPanel value={tab === 2} index={2}>
                <Trends client={client} />
              </TabPanel>
              <TabPanel value={tab === 3} index={3}>
                <AssessmentOverview client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 4} index={4}>
                <Schedule client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 5} index={5}>
                <ClientMobileSettings client={client} />
              </TabPanel>
            </>
          )}
          {!isAIServicesMember && !showBriteFeatures && (
            <>
              <TabPanel value={tab === 0} index={0}>
                <TrendsOverview client={client} openTrends={() => setTab(1)} />
                <div className={classes.chartsAndSchedule}>
                  <div>
                    <div
                      className={clsx(
                        classes.titleIcon,
                        classes.subtitleMargin
                      )}
                    >
                      <Typography variant="h3" className={classes.subtitle}>
                        Mood
                      </Typography>
                      <MoodHelpIcon
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                      />
                    </div>
                    <div className={classes.chart}>
                      <MoodBarChart
                        data={client.mood_measurements}
                        timePeriod={timePeriod}
                        setTimePeriod={setTimePeriod}
                      />
                    </div>
                    <Typography
                      variant="h3"
                      className={clsx(classes.subtitle, classes.subtitleMargin)}
                    >
                      Insights
                    </Typography>
                    <div className={classes.chart}>
                      <Insights checkIns={totalCheckins} client={client} />
                    </div>
                    <QuestionnairesOverview
                      client={client}
                      canSchedule={canSchedule}
                    />
                  </div>
                  <ScheduleOverview client={client} canSchedule={canSchedule} />
                </div>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{ paddingTop: "10px" }}
                >
                  <Button
                    variant="outlined"
                    onClick={exportToPdf}
                    style={{ textTransform: "none" }}
                    disabled={loading}
                  >
                    Export
                  </Button>
                </Grid>
              </TabPanel>
              <TabPanel value={tab === 1} index={1}>
                <Trends client={client} />
              </TabPanel>
              <TabPanel value={tab === 2} index={2}>
                <AssessmentOverview client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 3} index={3}>
                <Schedule client={client} canSchedule={canSchedule} />
              </TabPanel>
              <TabPanel value={tab === 4} index={4}>
                <ClientMobileSettings client={client} />
              </TabPanel>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    clientDetail: {
      paddingTop: HeaderHeight,
      backgroundColor: colors.neutral.white,
      flex: 1,
    },
    tabs: {
      backgroundColor: colors.neutral.white,
      color: "#545E78",
      boxShadow: "none",
      paddingLeft: 32,
      "& .MuiTabs-flexContainer": {
        gap: theme.spacing(1),
      },
      "& .MuiTab-root": {
        minWidth: "auto",
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        flexShrink: 0,
      },
      "& .MuiTabs-scrollButtons": {
        width: theme.spacing(4),
        height: theme.spacing(4),
        margin: theme.spacing(0, 0.5),
        borderRadius: "50%",
        color: colors.neutral.asphalt,
        backgroundColor: colors.primary.cloud,
        transition: theme.transitions.create(["background-color", "color"]),
        "&:hover": {
          color: colors.neutral.white,
          backgroundColor: colors.primary.periwinkle,
        },
        "&.Mui-disabled": {
          opacity: 0.35,
          backgroundColor: "transparent",
        },
      },
    },
    header: {
      height: 130,
      maxWidth: 1440,
      margin: "0 auto",
    },
    title: {
      color: colors.neutral.denim,
    },
    navigation: {
      height: 92,
      width: "fit-content",
      display: "flex",
      alignItems: "center",
      padding: 0,
      margin: 0,
      border: 0,
      backgroundColor: "transparent",
      cursor: "pointer",
      paddingLeft: theme.spacing(3),
      outline: "none",
    },
    contentContainer: {
      borderTop: `1px solid ${colors.neutral.clay}`,
      display: "flex",
      flex: 1,
      marginTop: 9,
      width: "100%",
      justifyContent: "center",
    },
    content: {
      maxWidth: 1440,
      padding: theme.spacing(4),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      width: "100%",
    },
    chartsAndSchedule: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      gap: theme.spacing(3),
    },
    chart: {
      marginBottom: theme.spacing(2),
    },
    loading: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
    },
    chevron: {
      marginLeft: 4,
    },
    subtitle: {
      marginRight: 4,
    },
    titleIcon: {
      display: "flex",
      alignItems: "center",
    },
    subtitleMargin: {
      marginBottom: theme.spacing(2),
    },
    distressRatings: {
      fontWeight: 400,
    },
    connectionStatusContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      paddingLeft: theme.spacing(4),
    },
    statusChip: {
      fontWeight: 600,
    },
    disconnectedChip: {
      backgroundColor: colors.neutral.clay,
      color: colors.neutral.asphalt,
      fontWeight: 600,
    },
    closeButton: {
      margin: 0,
      padding: 0,
      border: 0,
      height: theme.spacing(2),
      width: theme.spacing(2),
      backgroundColor: "transparent",
      cursor: "pointer",
      "&:focus": {
        outline: "none",
      },
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    ellipsisIcon: {
      display: "flex",
      width: "38px",
      height: "38px",
      padding: "10.76px 11.24px 11.24px 10.76px",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "360px",
      marginTop: "6px",
      backgroundColor: "rgba(0, 0, 0, 0.03)",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
      },
    },
  })
)

export default MyPractitionerClientDetail
